import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import '../../../estilos/vencimientos.css';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { consultarPanel } from '../api/vencimientosApi.js';
import {
  claseDeEstado, fecha as formatearFecha, iconoDeEstado, importe as formatearEnDivisa,
  inicialesDeTarjeta, nombreDeTarjeta,
} from '../formato.js';

/**
 * BLOQUE DE AVISOS DEL TABLERO (RF-122, RN-277, RN-278, RN-291, RN-292).
 *
 * MUESTRA SOLO LAS TARJETAS CON `avisoActivo` —las vencidas y las que entran en los
 * días de anticipación— Y NADA MÁS. SI NO HAY NINGUNA, EL BLOQUE NO SE DIBUJA: un
 * bloque vacío permanente enseña a la persona a ignorar ese rincón de la pantalla, y
 * entonces tampoco lo verá el día que sí diga algo.
 *
 * NO ORDENA, NO REAGRUPA, NO COMPARA FECHAS Y NO RECORTA LA LISTA (RN-292):
 * conserva el orden en que el servidor entrega las tarjetas y solo omite las que no
 * avisan. Un tope de renglones escondería un vencimiento, que es justo lo que
 * RN-260 prohíbe. Si este componente aplicara un `sort` propio sería defecto, aunque
 * el resultado coincidiera el día que se probó.
 *
 * CONSUME LA MISMA OPERACIÓN QUE LA PESTAÑA y muestra EXACTAMENTE las mismas cifras
 * (RN-278), con `incluirSinSaldo = false`: no se pierde ni una tarjeta visible,
 * porque una SIN_SALDO nunca tiene aviso (D-192, RN-290).
 *
 * NI UNA SOLA CIFRA DEL CORTE EN CURSO APARECE AQUÍ, en ninguna tarjeta y bajo
 * ninguna condición: no tiene estado, no avisa y todavía no se paga (RN-283).
 */
export function BloqueDeVencimientosDelTablero({ base = '/cuentas/vencimientos' }) {
  const hoy = obtenerFechaDeNegocioHoy();

  const consultar = useCallback(
    () => consultarPanel({ fechaLocal: hoy, incluirSinSaldo: false }),
    [hoy],
  );

  const listado = useConsultaDeListado(consultar, (r) => (r?.tarjetas ?? []).length === 0);

  // Mientras carga o si falla, el bloque simplemente no ocupa lugar: el Tablero no
  // depende de él y nunca se queda a medias.
  if (listado.estado !== ESTADO_DE_LISTADO.CON_DATOS) return null;

  const conAviso = (listado.datos?.tarjetas ?? []).filter((t) => t.avisoActivo);
  if (conAviso.length === 0) return null;

  const vencidas = conAviso.filter((t) => t.claveEstado === 'VENCIDA').length;

  return (
    <section className="card" aria-label="Vencimientos que necesitan tu atención"
      data-prueba="bloque-de-vencimientos-del-tablero">
      <div className="fila">
        <span className="crece"><b>Vencimientos que necesitan tu atención</b></span>
        <span className="chip" data-prueba="cantidad-con-aviso">{conAviso.length}</span>
      </div>

      {conAviso.map((tarjeta) => (
        <Link
          className="fila renglon-aviso"
          key={tarjeta.idCuenta}
          to={`${base}/${tarjeta.idCuenta}`}
          data-prueba="aviso-de-tarjeta"
          data-cuenta={tarjeta.idCuenta}
        >
          <span className="avat" aria-hidden="true">{inicialesDeTarjeta(tarjeta.nombreTarjeta)}</span>
          <span className="crece">
            <b>{nombreDeTarjeta(tarjeta)}</b>
            <small>de {tarjeta.nombrePropietario} · límite {formatearFecha(tarjeta.fechaLimitePago)}</small>
            <span className={`chip ${claseDeEstado(tarjeta.claveEstado)}`}>
              <span aria-hidden="true">{iconoDeEstado(tarjeta.claveEstado)}</span> {tarjeta.textoEstado}
            </span>
            {tarjeta.hayPagoParcial && (
              <small data-prueba="aviso-pago-parcial">
                Abonaste {formatearEnDivisa(tarjeta.importeCubierto, tarjeta.codigoISO)} de{' '}
                {formatearEnDivisa(tarjeta.importeExigible, tarjeta.codigoISO)}
              </small>
            )}
          </span>
          <span className="importe">
            <b data-prueba="aviso-importe-pendiente">
              {formatearEnDivisa(tarjeta.importePendiente, tarjeta.codigoISO)}
            </b>
            <small>por pagar</small>
          </span>
        </Link>
      ))}

      <div className="fila">
        <span className="crece ayuda">
          Solo salen las tarjetas que avisan hoy: {vencidas}
          {vencidas === 1 ? ' vencida' : ' vencidas'} y {conAviso.length - vencidas} dentro de tus
          días de anticipación. Las demás siguen en el panel.
        </span>
        <Link className="btn sec" to={base}>Ver todas</Link>
      </div>
    </section>
  );
}
